/**
 * Payment-Link Link-Tester — „c=1"-Format-Definition (Spec 503, Phase 1).
 *
 * Die echte, von der Facade gelieferte Format-Definition (Handover 2026-07-16,
 * gegen den Dienst-Quellcode belegt). Sie ersetzt perspektivisch `PENDING_FORMAT`
 * am Mount (Phase 7) — in Phase 1 ist sie NUR unit-getestet (der Mount bleibt
 * Teaser), damit der Testvektor-Anker ohne UI verifizierbar ist.
 *
 * Kern-Überraschung gegenüber dem UX-Design (Handover gewinnt): ES WERDEN ALLE
 * PARAMETER AUSSER `sig` SIGNIERT — auch Merchant-eigene (UTM etc.). Die richtige
 * Dimension ist daher „erreicht den Facade-Body" (Body-Reach), nicht „signiert vs.
 * frei". Diese Datei liefert das Body-Reach-Modell als neue Exporte; die
 * `ParamClass`-Union (`format-definition.ts`) + ihre Konsumenten (`verify.ts`,
 * `build-tab.tsx`, `verify-tab.tsx`, `link-url.ts`) werden erst in Phase 3/4
 * nachgezogen. `classifyParam` erfüllt hier bewusst noch den bestehenden
 * `ParamClass`-Vertrag (Zwischen-Mapping, in Phase 3 abgelöst).
 */
import type {
  BodyReach,
  FingerprintTransform,
  LinkParam,
  ParamClass,
  PaymentLinkFormatDefinition,
  ServicePrefill,
  SignatureInputs,
} from './format-definition';

/** Name des Signatur-Query-Parameters (Handover §1). */
export const C1_SIGNATURE_PARAM = 'sig';

/**
 * Klick-Endpunkt-Pfad des Payment-Link-Dienstes (Handover §1:
 * `GET https://pay.<stage>…/link/v1?<params>`). Teil des Format-Wissens: die
 * Pfad-Version `v1` korrespondiert mit dem Signatur-Schema `c=1`. Die Signatur
 * ist pfad-frei — der Pfad zählt NICHT zur Basis, sorgt aber dafür, dass der
 * gebaute Link tatsächlich routet (statt am Origin zu 404en).
 */
export const C1_CLICK_PATH = '/link/v1';

/**
 * Whitelist Link-Param → Facade-Body-Feld (Handover §1, vollständig). Nur diese
 * System-Parameter (plus `meta_`-Parameter) landen im Facade-Body. `m`/`c`/`sig`
 * erreichen den Body nie.
 */
export const C1_SYSTEM_PARAM_TO_BODY_FIELD: Readonly<Record<string, string>> = {
  svc: 'serviceId',
  cbOk: 'callbackOk',
  cbErr: 'callbackError',
  ref: 'customerReference',
  dest: 'destination',
  op: 'operatorId',
  flow: 'flowType',
  replyPath: 'replyPath',
  progressUrl: 'progressUpdateUrl',
  desc: 'description',
  amount: 'amount',
};

/** Pflicht-Parameter (Handover §1; `sig` ist der Signatur-Parameter selbst). */
export const C1_REQUIRED_PARAMS = ['svc', 'cbOk', 'm', 'c'] as const;

/** Präfix für Merchant-Metadaten, die bis zur Facade durchkommen (`metadata.<x>`). */
export const META_PREFIX = 'meta_';

/**
 * Body-Reach eines Parameters — die korrigierte Klassifikations-Dimension
 * (Handover §1). ALLE Fälle außer `sig` werden signiert; `Body-Reach` sagt, ob und
 * wie der Parameter den Facade-Body erreicht:
 * - `systemField`  System-Whitelist → mappt auf ein Body-Feld,
 * - `meta`         `meta_<x>` → `metadata.<x>`,
 * - `routing`      `m`/`c` → signiert & Pflicht, aber KEIN Body-Feld,
 * - `raw`          eigener roher Parameter → mitsigniert, erreicht die Facade nicht.
 *
 * (Der Typ selbst lebt in `format-definition.ts`, damit er auf dem Interface
 * verfügbar ist; hier re-exportiert für die bestehenden Importe.)
 */
export type { BodyReach } from './format-definition';

/** Hat dieser `meta_`-Key ein nicht-leeres Suffix? (`meta_` allein ist ungültig.) */
export function hasMetaSuffix(key: string): boolean {
  return key.startsWith(META_PREFIX) && key.length > META_PREFIX.length;
}

/**
 * Body-Reach eines Parameter-Keys. `m`/`c` sind Routing (signiert, Pflicht, kein
 * Body-Feld); ein bare `meta_` ohne Suffix ist kein gültiger Metadaten-Key und
 * zählt hier als `raw` (die Suffix-Validierung passiert separat in Phase 3).
 */
export function bodyReachOf(key: string): BodyReach {
  if (key === 'm' || key === 'c') {
    return 'routing';
  }
  if (Object.prototype.hasOwnProperty.call(C1_SYSTEM_PARAM_TO_BODY_FIELD, key)) {
    return 'systemField';
  }
  if (hasMetaSuffix(key)) {
    return 'meta';
  }
  return 'raw';
}

/**
 * Zielfeld im Facade-Body für die Diagnose/Badges: das gemappte Body-Feld für
 * System-Keys, `metadata.<x>` für `meta_`-Keys, sonst `undefined` (Routing/roh
 * erreichen den Body nicht).
 */
export function bodyFieldFor(key: string): string | undefined {
  if (Object.prototype.hasOwnProperty.call(C1_SYSTEM_PARAM_TO_BODY_FIELD, key)) {
    return C1_SYSTEM_PARAM_TO_BODY_FIELD[key];
  }
  if (hasMetaSuffix(key)) {
    return `metadata.${key.slice(META_PREFIX.length)}`;
  }
  return undefined;
}

/**
 * Byte-Vergleich zweier ASCII-Keys (Handover §2 Punkt 4): case-sensitiv,
 * Großbuchstaben vor Kleinbuchstaben. Für reine ASCII-Keys entspricht der
 * JS-`<`-Vergleich (UTF-16-Code-Units) exakt der Byte-Reihenfolge — der Testvektor
 * `c < cbOk < m < svc` ist der Anker.
 *
 * Grenze (dokumentiert, kein Fix nötig — Integrations-Review): Alle Parameter-Keys
 * sind ASCII (System-Whitelist + `meta_<ascii>`-Konvention). Nicht-BMP-Keys (z. B.
 * ein Emoji-Suffix in einem `meta_`-Key) würden in UTF-16-Code-Units anders sortiert
 * als in UTF-8-Byte-Reihenfolge; falls je relevant, hier auf `TextEncoder`-Byte-
 * Vergleich umstellen.
 */
function byteCompareKey(a: string, b: string): number {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
}

/**
 * Alle Nicht-`sig`-Parameter (System + frei). Der Signatur-Parameter `sig` wird
 * defensiv herausgefiltert — die Basis darf ihn NIE enthalten (Handover §2 Punkt
 * 3). Der Aufrufer entfernt ihn zwar bereits (`parsePastedUrl`), aber ein
 * durchgerutschtes `sig` würde sonst die Signatur verfälschen (das ist zugleich
 * der Fingerprint-Fehlerfall „`sig` mit in die Basis"); der Kanon selbst muss
 * dagegen robust sein.
 */
function nonSigPairs(inputs: SignatureInputs): LinkParam[] {
  return [...inputs.systemParams, ...inputs.freeParams].filter(
    (p) => p.key !== C1_SIGNATURE_PARAM,
  );
}

/** Byte-sortiert nach Key (ASCII, case-sensitiv). */
function byteSortByKey(pairs: LinkParam[]): LinkParam[] {
  return [...pairs].sort((x, y) => byteCompareKey(x.key, y.key));
}

/** Kanonische Zeilen-Form: je Paar `key=value\n`, inkl. Trailing-`\n`. */
function linesWithTrailing(pairs: LinkParam[]): string {
  return pairs.map((p) => `${p.key}=${p.value}\n`).join('');
}

/**
 * Kanonischer String-to-Sign (Handover §2, exakt): alle Nicht-`sig`-Parameter
 * (System + frei) zusammenfassen, byte-sortiert nach Key, je Paar `key=value\n`,
 * inkl. abschließendem `\n` nach dem letzten Paar. Werte sind bereits DEKODIERT
 * (die UI hält dekodierte Werte); Pfad/`?`/`&` sind nie Teil der Basis.
 */
function c1BuildStringToSign(inputs: SignatureInputs): string {
  return linesWithTrailing(byteSortByKey(nonSigPairs(inputs)));
}

/**
 * Vorbefüllte System-Parameter aus dem Service-Prefill (Handover §1 + Spec §1):
 * genau `svc`←`serviceId`, `m`←`merchantId`, konstant `c=1`. Alle übrigen
 * Whitelist-Parameter (`cbOk`, `cbErr`, `ref`, …) sind call-/nutzer-spezifisch und
 * werden NICHT vorbefüllt (`grossAmountCents`/`interval`/`displayName` sind rein
 * informativ für die Picker-Anzeige).
 */
function c1BuildSystemParams(prefill: ServicePrefill): LinkParam[] {
  return [
    { key: 'svc', value: prefill.serviceId },
    { key: 'm', value: prefill.merchantId },
    { key: 'c', value: '1' },
  ];
}

/**
 * Zwischen-Mapping auf den bestehenden `ParamClass`-Vertrag (Phase 1). Unter „c=1"
 * ist JEDER Nicht-`sig`-Parameter signiert; die alte `signed`/`free`-Trennung bildet
 * die Body-Reach-Dimension noch nicht ab. Bis die Konsumenten in Phase 3/4 auf
 * Body-Reach umgestellt sind, liefert diese Funktion die alten Preview-Eimer:
 * Body-erreichende/Routing-Parameter → `signed` (Akzent), rohe → `free` (neutral).
 * Kein Parameter ist `unknown` (das bleibt dem Teaser `PENDING_FORMAT` vorbehalten).
 */
function c1ClassifyParam(key: string): ParamClass {
  return bodyReachOf(key) === 'raw' ? 'free' : 'signed';
}

// ───────────────────────────────────────────────────────────────────────────
// Fingerprint-Katalog (Spec 503, Phase 2) — konkrete Fehler-Transformationen für
// „c=1". Jede Transform erzeugt aus den Eingaben die alternative Basis/den
// alternativen Schlüssel, die ein typischer Bau-Fehler ergäbe. Das Framework
// (fingerprint.ts) rechnet sie durch und meldet die, deren Signatur zur
// gelieferten passt (freundliche Diagnose-Copy je `diagnoseKey`, de+en in Phase 7).
// ───────────────────────────────────────────────────────────────────────────

/** i18n-Präfix der Diagnose-Copy. */
const DIAG = 'tester.diag';

/** Zeilen-Form OHNE Sortierung (Einfüge-/Wire-Reihenfolge). */
function linesInsertionOrder(pairs: LinkParam[]): string {
  return linesWithTrailing(pairs);
}

/** Case-insensitive alphabetische Sortierung (statt Byte-Reihenfolge). */
function caseInsensitiveSort(pairs: LinkParam[]): LinkParam[] {
  return [...pairs].sort((x, y) => {
    const a = x.key.toLowerCase();
    const b = y.key.toLowerCase();
    return a < b ? -1 : a > b ? 1 : 0;
  });
}

/** &-verkettete `key=value`-Kette (kein Trailing, `&` statt `\n`). */
function ampersandChain(pairs: LinkParam[]): string {
  return pairs.map((p) => `${p.key}=${p.value}`).join('&');
}

/**
 * Hex-String → rohe Bytes (`Uint8Array`). Modelliert den klassischen Fehler „Salt
 * hex-dekodiert statt als ASCII-Text" (Handover §3) EXAKT — die Bytes gehen als
 * roher Schlüssel in HMAC (`keyBytes`), nicht über `TextEncoder` (der High-Bytes
 * ≥ 0x80 zu 2-Byte-UTF-8 aufblähen würde). So feuert die Diagnose auch für echte
 * 64-Hex-Salts (Integrations-Review P1). `null`, wenn kein gültiges Hex.
 */
function hexToBytes(hex: string): Uint8Array | null {
  if (hex.length === 0 || hex.length % 2 !== 0 || !/^[0-9a-fA-F]+$/.test(hex)) {
    return null;
  }
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i += 1) {
    bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

/** Erzeugt je Pflicht-Parameter eine „vergessen mitzusignieren"-Transform. */
function missingRequiredTransforms(): FingerprintTransform[] {
  return C1_REQUIRED_PARAMS.map((reqKey) => ({
    // Die id trägt den Parameter-Namen — die UI (Phase 4) interpoliert {name} in
    // die gemeinsame Diagnose-Copy anhand dieses Suffix.
    id: `missingRequired:${reqKey}`,
    diagnoseKey: `${DIAG}.missingRequired`,
    transform: (inputs: SignatureInputs) => {
      const pairs = nonSigPairs(inputs);
      if (!pairs.some((p) => p.key === reqKey)) {
        return null; // Nicht vorhanden → nichts wegzulassen.
      }
      return {
        stringToSign: linesWithTrailing(
          byteSortByKey(pairs.filter((p) => p.key !== reqKey)),
        ),
      };
    },
  }));
}

/**
 * Der vollständige „c=1"-Fingerprint-Katalog. Reihenfolge = Melde-Reihenfolge.
 * Aliasing ist gewollt: „Trailing-`\n` vergessen" und „`printf` vs. `$()`"
 * rechnen identisch und feuern beide (die Spec will „alle plausiblen Ursachen").
 */
const C1_TRANSFORMS: FingerprintTransform[] = [
  {
    id: 'sortWire',
    diagnoseKey: `${DIAG}.sortWire`,
    transform: (inputs) => ({
      stringToSign: linesInsertionOrder(nonSigPairs(inputs)),
    }),
  },
  {
    id: 'sortCaseInsensitive',
    diagnoseKey: `${DIAG}.sortCaseInsensitive`,
    transform: (inputs) => ({
      stringToSign: linesWithTrailing(caseInsensitiveSort(nonSigPairs(inputs))),
    }),
  },
  {
    id: 'encodedValues',
    diagnoseKey: `${DIAG}.encodedValues`,
    transform: (inputs) => ({
      stringToSign: linesWithTrailing(
        byteSortByKey(nonSigPairs(inputs)).map((p) => ({
          key: p.key,
          value: encodeURIComponent(p.value),
        })),
      ),
    }),
  },
  {
    id: 'sigInBasis',
    diagnoseKey: `${DIAG}.sigInBasis`,
    // Modelliert „ein `sig=`-Platzhalter in der signierten Basis". Der echte
    // sig-Wert ist beim Signieren nicht bekannt (Chicken-Egg) → leerer Platzhalter.
    transform: (inputs) => ({
      stringToSign: linesWithTrailing(
        byteSortByKey([
          ...nonSigPairs(inputs),
          { key: C1_SIGNATURE_PARAM, value: '' },
        ]),
      ),
    }),
  },
  {
    id: 'rawOmitted',
    diagnoseKey: `${DIAG}.rawOmitted`,
    // V6-INVERSION: eigene rohe Parameter NICHT mitsigniert (der Fehlerfall ist
    // „vergessen mitzusignieren"). „raw" wird intern über bodyReachOf bestimmt.
    transform: (inputs) => {
      const pairs = nonSigPairs(inputs);
      const kept = pairs.filter((p) => bodyReachOf(p.key) !== 'raw');
      if (kept.length === pairs.length) {
        return null; // Keine rohen Parameter → kein distinkter Fehler.
      }
      return { stringToSign: linesWithTrailing(byteSortByKey(kept)) };
    },
  },
  {
    id: 'trailingNewlineMissing',
    diagnoseKey: `${DIAG}.trailingNewlineMissing`,
    transform: (inputs) => {
      const base = c1BuildStringToSign(inputs);
      return { stringToSign: base.endsWith('\n') ? base.slice(0, -1) : base };
    },
  },
  {
    id: 'ampersandConcat',
    diagnoseKey: `${DIAG}.ampersandConcat`,
    transform: (inputs) => ({
      stringToSign: ampersandChain(byteSortByKey(nonSigPairs(inputs))),
    }),
  },
  {
    id: 'pathIncluded',
    diagnoseKey: `${DIAG}.pathIncluded`,
    // Pfad + `?` + &-Kette mit-signiert (statt nur der `key=value\n`-Zeilen).
    transform: (inputs) => ({
      stringToSign: `/link/v1?${ampersandChain(byteSortByKey(nonSigPairs(inputs)))}`,
    }),
  },
  {
    id: 'saltHexDecoded',
    diagnoseKey: `${DIAG}.saltHexDecoded`,
    // Der Klassiker (Handover §3): Salt als Hex-Zahl dekodiert statt als Text.
    // Als ROHE Bytes (keyBytes) — exakt auch für echte 64-Hex-Salts mit High-Bytes.
    transform: (_inputs, canonicalKey) => {
      const bytes = hexToBytes(canonicalKey);
      return bytes === null ? null : { keyBytes: bytes };
    },
  },
  {
    id: 'saltTrailingNewline',
    diagnoseKey: `${DIAG}.saltTrailingNewline`,
    // Der klassische 401: ein Trailing-Newline im eingegebenen Salt.
    transform: (_inputs, canonicalKey) => ({ key: `${canonicalKey}\n` }),
  },
  {
    id: 'saltUppercase',
    diagnoseKey: `${DIAG}.saltUppercase`,
    transform: (_inputs, canonicalKey) => ({ key: canonicalKey.toUpperCase() }),
  },
  ...missingRequiredTransforms(),
  {
    id: 'printfVsDollarParen',
    diagnoseKey: `${DIAG}.printfVsDollarParen`,
    // Deckt sich mit trailingNewlineMissing: `$(…)` in der Shell strippt das
    // abschließende `\n` der Basis. Feuert bewusst zusätzlich (Shell-Kontext).
    transform: (inputs) => {
      const base = c1BuildStringToSign(inputs);
      return { stringToSign: base.endsWith('\n') ? base.slice(0, -1) : base };
    },
  },
];

/**
 * Die „c=1"-Format-Definition. `available:true`; alle scharfen Methoden rechnen
 * gegen den Handover-Kanon. `transforms` = der vollständige Fingerprint-Katalog
 * (Phase 2). Der Großbuchstaben-`sig`-Sonderfall ist KEIN Transform (er urteilt
 * über die Roh-Eingabe) und sitzt in `verify.ts`.
 */
export const FORMAT_C1: PaymentLinkFormatDefinition = {
  available: true,
  signatureParamName: C1_SIGNATURE_PARAM,
  signaturePlacement: 'query-append',
  clickPath: C1_CLICK_PATH,
  requiredParams: C1_REQUIRED_PARAMS,
  transforms: C1_TRANSFORMS,
  testVector: {
    salt: '4a6f686e',
    systemParams: [
      { key: 'svc', value: 'SVC_123' },
      { key: 'cbOk', value: 'https://ok.com' },
      { key: 'm', value: 'gameloft' },
      { key: 'c', value: '1' },
    ],
    freeParams: [],
    expectedSignature:
      '136a727ed8711274606741171601fabea23d35b70d0db586fa0c4a47211f3eb7',
  },
  buildStringToSign: c1BuildStringToSign,
  buildSystemParams: c1BuildSystemParams,
  classifyParam: c1ClassifyParam,
  bodyReachOf,
  bodyFieldOf: bodyFieldFor,
};
